import React, { useEffect, useRef, useState } from 'react';

const AdvancedGoogleMap = ({ offices, selectedLocation, onMarkerClick }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapError, setMapError] = useState(null);
  const [isCurrentlyExpanded, setIsCurrentlyExpanded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Extract coordinates from Google Maps URL
  const extractCoordinates = (url) => {
    if (!url) return null;

    try {
      const preciseMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
      if (preciseMatch) {
        return {
          lat: parseFloat(preciseMatch[1]),
          lng: parseFloat(preciseMatch[2]),
        };
      }

      const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (coordMatch) {
        return {
          lat: parseFloat(coordMatch[1]),
          lng: parseFloat(coordMatch[2]),
        };
      }

      const queryMatch = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (queryMatch) {
        return {
          lat: parseFloat(queryMatch[1]),
          lng: parseFloat(queryMatch[2]),
        };
      }

      return null;
    } catch (error) {
      console.error('Error extracting coordinates:', error);
      return null;
    }
  };

  // Build marker with both simple icon and details (details hidden by default)
  const buildMarkerContent = (office) => {
    const content = document.createElement('div');
    content.classList.add('office-marker-container');
    content.style.position = 'relative';

    // Build full address for directions
    const fullAddress = [office.address, office.city, office.country]
      .filter(Boolean)
      .join(', ');

    // Build directions URL using coordinates or address
    const directionsUrl = office.position
      ? `https://www.google.com/maps/dir/?api=1&destination=${office.position.lat},${office.position.lng}`
      : fullAddress
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`
        : null;

    content.innerHTML = `
      <div class="office-marker-simple">
        <div class="simple-marker-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="92" height="92" viewBox="0 0 24 24" fill="currentColor" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
            <circle cx="12" cy="10" r="3" fill="white"></circle>
          </svg>
        </div>
      </div>
      <div class="office-marker-detailed" style="display: none; position: absolute; bottom: 100%; left: 150%; transform: translate(-140% , 60%); margin-bottom: 10px;">
        <button class="marker-close-btn" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div class="marker-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </div>
        <div class="marker-details">
          <div class="office-name">${office.name}</div>
          ${office.address ? `<div class="office-address">${office.address}</div>` : ''}
          ${office.city && office.city !== office.name ? `<div class="office-city">${office.city}</div>` : ''}
          ${office.country ? `<div class="office-country">${office.country}</div>` : ''}
          ${office.phone ? `
  <div class="office-contact office-contact-phone">
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>

    ${office.phone
          .split('/')
          .map(raw => {
            const clean = raw.trim().replace(/[^\d+]/g, '');
            return `
          <span 
            class="phone-number" 
            data-phone="${clean}"
          >
            ${raw.trim()}
          </span>
        `;
          })
          .join('<span class="phone-separator"> / </span>')
        }
  </div>
` : ''}

          ${office.email ? `
            <div class="office-contact">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
              </svg>
              <span>${office.email}</span>
            </div>
          ` : ''}
          ${office.openingHours ? `
            <div class="office-contact">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>${office.openingHours}</span>
            </div>
          ` : ''}
          ${directionsUrl ? `
            <button class="get-directions-btn" data-directions-url="${directionsUrl}">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
              </svg>
              Get Directions
            </button>
          ` : ''}
        </div>
      </div>
    `;

    return content;
  };

  // Toggle marker between simple and detailed view
  const toggleMarkerView = (marker, office, forceCollapse = false) => {
    const detailedPanel = marker.content.querySelector('.office-marker-detailed');
    const isExpanded = detailedPanel.style.display !== 'none';

    // Collapse all other markers
    markersRef.current.forEach((m) => {
      if (m !== marker && m.content) {
        const otherDetailedPanel = m.content.querySelector('.office-marker-detailed');
        if (otherDetailedPanel) {
          otherDetailedPanel.style.display = 'none';
        }
        m.zIndex = null;
      }
    });

    if (forceCollapse || isExpanded) {
      // Collapse this marker
      detailedPanel.style.display = 'none';
      marker.zIndex = null;
      setIsCurrentlyExpanded(false);
    } else {
      // Expand this marker
      detailedPanel.style.display = 'block';
      marker.zIndex = 1;
      setIsCurrentlyExpanded(true);

      // Notify parent component
      if (onMarkerClick) {
        onMarkerClick(office);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    const initMap = async () => {
      try {
        // Wait for component to be mounted
        if (!isMounted || !mapRef.current) {
          return;
        }

        // Check if Google Maps is available
        if (typeof window === 'undefined' || !window.google) {
          if (isMounted) {
            setMapError('Google Maps API is not loaded. Please check your API key.');
          }
          return;
        }

        if (!window.google.maps) {
          if (isMounted) {
            setMapError('Google Maps library is not available.');
          }
          return;
        }

        const google = window.google;

        const validOffices = offices
          .map((office) => ({
            ...office,
            position: extractCoordinates(office.googleMapUrl),
          }))
          .filter((office) => office.position);

        if (validOffices.length === 0) {
          if (isMounted) {
            setMapError('No valid coordinates found in office data.');
          }
          return;
        }

        // Determine initial center based on selected location
        let initialCenter;
        let initialZoom = 14;

        if (selectedLocation) {
          const selectedOffice = validOffices.find(office => office.name === selectedLocation);
          if (selectedOffice) {
            initialCenter = selectedOffice.position;
          } else {
            initialCenter = validOffices[0].position;
          }
        } else {
          initialCenter = validOffices[0].position;
        }

        // Import libraries with error handling
        let Map, AdvancedMarkerElement;

        try {
          if (!isMounted) return;
          const mapsLib = await google.maps.importLibrary('maps');
          if (!isMounted) return;
          Map = mapsLib.Map;
        } catch (error) {
          console.error('Error loading maps library:', error);
          if (isMounted) {
            setMapError('Failed to load Google Maps. Please refresh the page.');
          }
          return;
        }

        try {
          if (!isMounted) return;
          const markerLib = await google.maps.importLibrary('marker');
          if (!isMounted) return;
          AdvancedMarkerElement = markerLib.AdvancedMarkerElement;
        } catch (error) {
          console.error('Error loading marker library:', error);
          if (isMounted) {
            setMapError('Failed to load map markers. Please refresh the page.');
          }
          return;
        }

        if (!isMounted || !mapRef.current) return;

        const map = new Map(mapRef.current, {
          zoom: initialZoom,
          center: initialCenter,
          mapId: 'kangaroo-offices-map',
          gestureHandling: 'cooperative',
          disableDefaultUI: false,
          clickableIcons: false,   // Prevent dragging issues on mobile
        });

        mapInstanceRef.current = map;

        // Clean up old markers
        markersRef.current.forEach((marker) => {
          try {
            if (marker && marker.map) {
              marker.map = null;
            }
          } catch (e) {
            console.log('Error cleaning marker:', e);
          }
        });
        markersRef.current = [];

        validOffices.forEach((office) => {
          try {
            if (!isMounted) return;

            // Create marker with both simple and detailed content
            const markerContent = buildMarkerContent(office);

            const marker = new AdvancedMarkerElement({
              map,
              position: office.position,
              content: markerContent,
              title: office.name,
            });

            // Store office data in marker for later reference
            marker.officeData = office;

            // Prevent map dragging when interacting with marker content
            const detailedPanel = markerContent.querySelector('.office-marker-detailed');
            if (detailedPanel) {
              // Prevent map drag when mouse is over the detailed panel
              detailedPanel.addEventListener('mousedown', (e) => {
                e.stopPropagation();
              });

              detailedPanel.addEventListener('touchstart', (e) => {
                e.stopPropagation();
              }, { passive: false });

              detailedPanel.addEventListener('mousemove', (e) => {
                e.stopPropagation();
              });

              detailedPanel.addEventListener('touchmove', (e) => {
                e.stopPropagation();
              }, { passive: false });
            }

            // Add click listener to the simple marker icon
            const simpleMarker = markerContent.querySelector('.office-marker-simple');
            if (simpleMarker) {
              simpleMarker.addEventListener('click', (e) => {
                try {
                  e.stopPropagation();
                  toggleMarkerView(marker, office);
                } catch (err) {
                  console.log('Marker click error:', err);
                }
              });
            }

            // Add click listener to close button
            const closeBtn = markerContent.querySelector('.marker-close-btn');
            if (closeBtn) {
              closeBtn.addEventListener('click', (e) => {
                try {
                  e.stopPropagation();
                  toggleMarkerView(marker, office, true);
                } catch (err) {
                  console.log('Close button error:', err);
                }
              });
            }

            // Add click listener to Get Directions button
            const directionsBtn = markerContent.querySelector('.get-directions-btn');
            if (directionsBtn) {
              directionsBtn.addEventListener('click', (e) => {
                try {
                  e.stopPropagation();
                  const url = directionsBtn.getAttribute('data-directions-url');
                  if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }
                } catch (err) {
                  console.log('Directions button error:', err);
                }
              });
            }

            // Add click listener to phone contact - opens dialer ONLY on mobile
            const phoneContact = markerContent.querySelector('.office-contact-phone');

            if (phoneContact) {
              phoneContact.addEventListener('click', (e) => {
                try {
                  e.stopPropagation();

                  const target = e.target.closest('.phone-number');
                  if (!target) return;

                  const screenWidth =
                    window.innerWidth || document.documentElement.clientWidth || 768;

                  const isMobile = screenWidth <= 768;

                  if (isMobile) {
                    const phoneNumber = target.dataset.phone;

                    if (phoneNumber) {
                      // IMPORTANT: use location.href (works best on mobile Safari + Chrome)
                      window.location.href = `tel:${phoneNumber}`;
                    }
                  }
                } catch (err) {
                  console.log('Phone click error:', err);
                }
              });
            }



            if (!isMounted) return;
            markersRef.current.push(marker);

            // Auto-expand marker on initial load if it matches selectedLocation
            if (selectedLocation && office.name === selectedLocation) {
              timeoutId = setTimeout(() => {
                if (!isMounted) return;
                try {
                  const detailedPanel = markerContent.querySelector('.office-marker-detailed');
                  if (detailedPanel) {
                    detailedPanel.style.display = 'block';
                    marker.zIndex = 1;
                    setIsCurrentlyExpanded(true);

                    if (onMarkerClick) {
                      onMarkerClick(office);
                    }
                  }
                } catch (err) {
                  console.log('Auto-expand error:', err);
                }
              }, 100);
            }
          } catch (markerError) {
            console.error('Error creating marker for office:', office.name, markerError);
          }
        });

        if (isMounted) {
          setMapError(null);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Map initialization error:', error);
        if (isMounted) {
          setMapError(`Error loading map: ${error.message || 'Unknown error'}`);
        }
      }
    };

    // Add a delay to ensure DOM and scripts are ready
    timeoutId = setTimeout(() => {
      if (isMounted) {
        initMap();
      }
    }, 200);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      markersRef.current.forEach((marker) => {
        try {
          if (marker && marker.map) {
            marker.map = null;
          }
        } catch (e) {
          // Silent cleanup
        }
      });
      markersRef.current = [];
    };
  }, [offices, selectedLocation]);

  if (mapError) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        minHeight: '400px',
        backgroundColor: '#f8f9fa',
        border: '2px dashed #dee2e6',
        borderRadius: '8px'
      }}>
        <p style={{ color: '#6c757d', fontSize: '14px' }}>⚠️ {mapError}</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Map container */
        #map {
          width: 100%;
          height: 100%;
          min-height: 400px;
        }

        /* Marker container */
        .office-marker-container {
          position: relative;
          cursor: pointer;
        }

        /* Simple Pinpoint Marker (Collapsed State) */
        .office-marker-simple {
          position: relative;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .office-marker-simple:hover {
          transform: scale(1.15);
        }

        .simple-marker-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          color: #7D178F;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
        }

        .simple-marker-icon svg {
          width: 52px;
          height: 52px;
        }

        /* Detailed Marker (Expanded State) */
        .office-marker-detailed {
          position: relative;
          background: #fff;
          border-radius: 12px;
          padding: 12px 16px;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
          cursor: default;
          transition: all 0.3s ease;
          min-width: 200px;
          max-width: 320px;
          animation: markerExpand 0.3s ease-out;
          user-select: text;
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
          pointer-events: auto;
          touch-action: auto;
        }

        .office-marker-detailed * {
          user-select: text;
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
        }

        .office-marker-detailed button,
        .office-marker-detailed svg {
          user-select: none;
          -webkit-user-select: none;
        }

        @keyframes markerExpand {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        /* Close Button */
        .marker-close-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(0, 0, 0, 0.1);
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 10;
        }

        .marker-close-btn:hover {
          background: rgba(0, 0, 0, 0.2);
          transform: scale(1.1);
        }

        .marker-close-btn svg {
          width: 14px;
          height: 14px;
          color: #495057;
        }

        .marker-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 8px;
          color: #7D178F;
        }

        .office-marker.highlight .marker-icon {
          color: white;
        }

        .marker-icon svg {
          width: 28px;
          height: 28px;
        }

        .marker-details {
          text-align: left;
          user-select: text;
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
        }

        .office-name {
          font-weight: 700;
          font-size: 15px;
          margin-bottom: 6px;
          color: #212529;
          line-height: 1.3;
          user-select: text;
          -webkit-user-select: text;
        }

        .office-marker.highlight .office-name {
          color: white;
        }

        .office-address {
          font-size: 12px;
          color: #495057;
          margin-bottom: 4px;
          line-height: 1.4;
          user-select: text;
          -webkit-user-select: text;
        }

        .office-marker.highlight .office-address {
          color: rgba(255, 255, 255, 0.95);
        }

        .office-city {
          font-size: 12px;
          color: #6c757d;
          margin-bottom: 4px;
          font-weight: 500;
          user-select: text;
          -webkit-user-select: text;
        }

        .office-marker.highlight .office-city {
          color: rgba(255, 255, 255, 0.9);
        }

        .office-country {
          font-size: 12px;
          color: #687c86;
          margin-bottom: 8px;
          font-weight: 500;
          user-select: text;
          -webkit-user-select: text;
        }

        .office-marker.highlight .office-country {
          color: rgba(255, 255, 255, 0.9);
        }

        .office-contact {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #495057;
          margin-bottom: 4px;
          line-height: 1.4;
          user-select: text;
          -webkit-user-select: text;
        }

        .office-marker.highlight .office-contact {
          color: rgba(255, 255, 255, 0.95);
        }

        .office-contact:last-child {
          margin-bottom: 0;
        }

        .office-contact svg {
          flex-shrink: 0;
          opacity: 0.7;
          user-select: none;
        }

        .office-marker.highlight .office-contact svg {
          opacity: 1;
        }

        .office-contact span {
          word-break: break-word;
          user-select: text;
          -webkit-user-select: text;
        }

        /* Phone contact - clickable ONLY on mobile (≤768px) */
        .office-contact-phone {
          cursor: default;
        }

        @media (max-width: 768px) {
          .office-contact-phone {
            cursor: pointer;
            transition: color 0.2s ease;
          }

          .office-contact-phone:active {
            color: #007bff;
          }

          .office-contact-phone:active svg {
            color: #007bff;
          }
        }

        /* Get Directions button */
        .get-directions-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          margin-top: 8px;
          transition: background-color 0.2s ease;
          width: 100%;
          user-select: none;
          -webkit-user-select: none;
        }

        .get-directions-btn:hover {
          background-color: #0056b3;
        }

        .get-directions-btn:active {
          background-color: #004085;
        }

        .get-directions-btn svg {
          flex-shrink: 0;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .office-marker-detailed {
            min-width: 200px;
            max-width: 280px;
            padding: 12px 14px;
          }

          .office-name {
            font-size: 14px;
          }

          .office-address,
          .office-city,
          .office-country,
          .office-contact {
            font-size: 11px;
          }

          .get-directions-btn {
            font-size: 13px;
            padding: 9px 14px;
          }
        }
      `}</style>
      <div
        ref={mapRef}
        id="map"
        style={{ width: '100%', height: '100%', minHeight: '400px' }}
      />
    </>
  );
};

export default AdvancedGoogleMap;